import { Directive, ElementRef, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AnalyticsService } from '../services/analytics.service';
import { FeatureFlagService } from '../services/feature-flag.service';

/**
 * Captures business-critical user interactions (CTA clicks, link navigation)
 * on explicitly annotated elements and sends structured telemetry to Grafana
 * Faro. Behaviour follows Webinteractions v0.95.
 *
 * Usage:
 *   <button faroAction>Save as draft</button>
 *   <a faroAction="See all offerings" routerLink="/all-offerings">All Offerings</a>
 *   <button faroAction [faroActionAttrs]="{ offeringType: 'infrastructure' }">Request server</button>
 *
 * Only elements carrying the `faroAction` attribute emit telemetry. The whole
 * mechanism is gated behind the `panorama_inexp_2080_web_interactions` flag.
 */
@Directive({
  selector: '[faroAction]',
  host: {
    '(click)': 'onActivate($event)',
  },
})
export class FaroActionDirective {
  /** Optional custom context text attached as the `context` attribute. */
  readonly faroAction = input<string>('');
  /** Additional key-value pairs merged into the emitted event attributes. */
  readonly faroActionAttrs = input<Record<string, string>>({});

  private readonly _el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _routerLink = inject(RouterLink, {
    optional: true,
    self: true,
  });
  private readonly _featureFlags = inject(FeatureFlagService);
  private readonly _analytics = inject(AnalyticsService);

  /** Delay before navigating away on external links, so Faro can flush. */
  private static readonly EXTERNAL_NAV_DELAY_MS = 300;
  private static readonly MAX_LABEL_LENGTH = 80;
  private static readonly FEATURE_FLAG =
    'panorama_inexp_2080_web_interactions';
  private static readonly FALLBACK_LABEL = '(unlabeled)';

  onActivate(event: Event): void {
    if (!this._featureFlags.isEnabled(FaroActionDirective.FEATURE_FLAG)) {
      return;
    }

    const host = this._el.nativeElement;
    const name = this.resolveLabel(host);

    const attrs: Record<string, string> = {
      ...this.buildBaseAttrs(host),
      ...this.faroActionAttrs(),
      trigger: 'pointer',
      originPage: window.location.pathname,
    };

    const context = this.faroAction().trim();
    if (context) {
      attrs['context'] = context;
    }

    if (attrs['linkType'] === 'external') {
      // Stop the browser leaving before Faro can flush, emit, then navigate.
      event.preventDefault();
      this._analytics.pushUserActionEvent(name, attrs);
      this.navigateExternal(host as HTMLAnchorElement);
      return;
    }

    this._analytics.pushUserActionEvent(name, attrs);
  }

  /** Navigate to an external link after a short delay so Faro's transport can
   *  flush the event first. */
  private navigateExternal(host: HTMLAnchorElement): void {
    const { href, target } = host;
    setTimeout(() => {
      if (target === '_blank') {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = href;
      }
    }, FaroActionDirective.EXTERNAL_NAV_DELAY_MS);
  }

  /** Resolve a human-readable, normalized action name from the host element. */
  private resolveLabel(el: HTMLElement): string {
    const raw =
      el.getAttribute('aria-label') ||
      el.innerText ||
      el.getAttribute('title') ||
      el.querySelector('img')?.getAttribute('alt') ||
      el.getAttribute('alt');

    if (!raw) {
      return FaroActionDirective.FALLBACK_LABEL;
    }

    return this.normalize(raw);
  }

  /** Normalization rules (Webinteractions v0.95 §naming convention):
   *  trim, strip leading/trailing punctuation & symbols, hyphenate spaces,
   *  lowercase, truncate to 80 chars with an ellipsis, fall back when empty. */
  private normalize(raw: string): string {
    const normalized = raw
      .trim()
      .replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '')
      .replace(/\s+/g, '-')
      .toLowerCase();

    if (normalized.length === 0) {
      return FaroActionDirective.FALLBACK_LABEL;
    }
    if (normalized.length > FaroActionDirective.MAX_LABEL_LENGTH) {
      return (
        normalized.slice(0, FaroActionDirective.MAX_LABEL_LENGTH) + '…'
      );
    }
    return normalized;
  }

  /** Base attributes; `<a>` elements additionally get `href` and `linkType`. */
  private buildBaseAttrs(el: HTMLElement): Record<string, string> {
    const tag = el.tagName.toLowerCase();
    const attrs: Record<string, string> = { tag };

    if (tag !== 'a') {
      return attrs;
    }

    const href =
      this._routerLink?.urlTree?.toString() || el.getAttribute('href');

    if (!href) {
      attrs['linkType'] = 'missing';
      return attrs;
    }

    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) {
        attrs['href'] = url.hostname + url.pathname.replace(/\/+$/, '');
        attrs['linkType'] = 'external';
      } else {
        attrs['href'] = url.pathname;
        attrs['linkType'] = 'internal';
      }
    } catch {
      attrs['href'] = href;
      attrs['linkType'] = 'unknown';
    }

    return attrs;
  }
}
